// Note: design spec uses #32261D for tile text + icon background, but that exact
// hex is not in the palette. Using `espresso` (#30261E) as the closest available
// token.
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import utc from 'dayjs/plugin/utc'
import pluralize from 'pluralize'
import Slider from 'react-slick'
import 'slick-carousel/slick/slick-theme.css'
import 'slick-carousel/slick/slick.css'

import { GiftBox, TreePlanting } from 'src/components/atoms/Icons'
import TypographyRebrand from 'src/components/atoms/TypographyRebrand/TypographyRebrand'

import {
  ListUserEventsQuery,
  useListUserEventsQuery
} from 'src/config/generated/graphql'
import { ActivitySliderRebrand as ActivitySliderRebrandSchema } from 'src/config/generated/sanitySchema'
import { PurchaseType } from 'src/types/purchase'
import { UserEventType } from 'src/types/user'

dayjs.extend(relativeTime)
dayjs.extend(utc)

const SLIDER_LIMIT = 30

const SLIDER_SETTINGS = {
  arrows: false,
  dots: false,
  infinite: true,
  autoplay: true,
  autoplaySpeed: 0,
  speed: 7000,
  cssEase: 'linear',
  slidesToShow: 4,
  slidesToScroll: 1,
  variableWidth: true,
  pauseOnHover: false,
  pauseOnFocus: false,
  swipe: true
}

const getActionLabel = (event: ListUserEventsQuery['userEvents'][number]) => {
  if (event.Type === UserEventType.Planted) return 'planted'
  switch (event.PurchaseType) {
    case PurchaseType.FundedTree:
    case PurchaseType.RegistryGift:
      return 'donated'
    case PurchaseType.GiftedTree:
    case PurchaseType.BulkGifted:
      return 'gifted'
    default:
      return null
  }
}

const ActivitySliderRebrand = ({ compactTop }: ActivitySliderRebrandSchema) => {
  const { data } = useListUserEventsQuery({
    variables: { limit: SLIDER_LIMIT, offset: 0 }
  })

  const tiles = (data?.userEvents ?? [])
    .map(event => {
      const action = getActionLabel(event)
      if (!action) return null
      const quantity = event.Quantity ?? 0
      const name = event.Name || 'Anonymous'
      return {
        id: event.Id,
        title: `${quantity} ${pluralize('tree', quantity)} ${action}`,
        name,
        createdOn: event.CreatedOn,
        action
      }
    })
    .filter((tile): tile is NonNullable<typeof tile> => tile !== null)

  if (tiles.length === 0) return null

  return (
    <section className={`overflow-hidden ${compactTop ? '-mt-8' : ''}`}>
      <Slider {...SLIDER_SETTINGS}>
        {tiles.map(tile => (
          <div key={tile.id} className='pr-4'>
            <div className='bg-peachPale px-8 py-5 flex items-start justify-between w-[374px]'>
              <div className='flex flex-col gap-2 min-w-0 flex-1 pr-4 break-words text-espresso'>
                <TypographyRebrand variant='h5' color='espresso'>
                  {tile.title}
                </TypographyRebrand>
                <p className='font-body-rebrand text-base leading-[1.6] m-0 truncate'>
                  by {tile.name}
                </p>
                {tile.createdOn && (
                  <p className='font-body-rebrand text-xs leading-[1.6] m-0'>
                    {dayjs().to(dayjs.utc(tile.createdOn))}
                  </p>
                )}
              </div>
              <div className='shrink-0 bg-espresso text-peachPale rounded-xl h-10 w-10 flex items-center justify-center'>
                {tile.action === 'gifted' ? (
                  <GiftBox className='h-6 w-6' />
                ) : (
                  <TreePlanting className='h-6 w-6' />
                )}
              </div>
            </div>
          </div>
        ))}
      </Slider>
    </section>
  )
}

export default ActivitySliderRebrand
